import {
  Component,
  computed,
  effect,
  input,
  output,
  DestroyRef,
  HostListener,
  ElementRef,
  ViewChild,
  signal,
  inject,
  Injector,
  afterNextRender,
  afterEveryRender,
} from '@angular/core';
import { AssistantSupportService } from './assistant-support.service';
import { AssistantConversationService } from './assistant-conversation.service';
import { AssistantIntroductionService } from './assistant-introduction.service';
import { AssistantPageAvailability } from './assistant-page-availability.service';
import { AssistantVoiceService } from './assistant-voice.service';
import type { AssistantMessage } from './assistant.models';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ASSISTANT_CONTEXT } from './assistant-context';
import { ASSISTANT_SUPPORT_EMAIL } from './assistant.config';
import { assistantScreenLinks } from './assistant-navigation';
import { MovableAssistantDirective } from './movable-assistant.directive';
import { IconComponent, IconName } from '../../icon';

@Component({
  selector: 'app-assistant-panel',
  imports: [FormsModule, RouterLink, IconComponent, MovableAssistantDirective],
  templateUrl: './assistant-panel.html',
  styleUrls: ['./assistant-panel.scss', './assistant-controls.scss', './assistant-support.scss'],
})
export class AssistantPanel {
  @ViewChild('launcher') private launcher?: ElementRef<HTMLButtonElement>;
  @ViewChild(MovableAssistantDirective) private launcherPosition?: MovableAssistantDirective;
  protected readonly support = inject(AssistantSupportService);
  protected readonly voice = inject(AssistantVoiceService);
  @ViewChild('conversation') private conversation?: ElementRef<HTMLDivElement>;
  @ViewChild('composer') private composer?: ElementRef<HTMLTextAreaElement>;
  @ViewChild('endDialog') private endDialog?: ElementRef<HTMLDialogElement>;
  readonly authenticated = input(true);
  readonly dockArea = input<HTMLElement | null>(null);
  readonly dockClearanceChange = output<number>();
  @ViewChild('launcherArea') private launcherArea?: ElementRef<HTMLElement>;
  @ViewChild('introductionBubble') private introductionBubble?: ElementRef<HTMLElement>;
  protected readonly dockLeft = signal(24);
  protected readonly dockWidth = signal(0);
  protected readonly introductionWidth = computed(() => Math.max(0, this.dockWidth() - 72));
  protected readonly launcherBounds = () => this.launcherArea?.nativeElement.getBoundingClientRect() ?? null;
  private dockObserver?: ResizeObserver;
  private observedDockElements: HTMLElement[] = [];
  private lastDockClearance = 0;
  protected readonly pageAvailability = inject(AssistantPageAvailability);
  private readonly introduction = inject(AssistantIntroductionService);
  protected readonly introductionVisible = signal(false);
  private introductionTimer?: ReturnType<typeof setTimeout>;
  private readonly introductionDelayMs = 2000;
  readonly open = signal(false);
  protected readonly maximized = signal(false);
  protected readonly conversationState = inject(AssistantConversationService);
  private readonly injector = inject(Injector);
  private readonly readContext = inject(ASSISTANT_CONTEXT);
  private readonly supportEmail = inject(ASSISTANT_SUPPORT_EMAIL);
  // Only deployment configuration can supply this address; model text is never linkified.
  protected readonly supportHref = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(
    this.supportEmail,
  )
    ? `mailto:${this.supportEmail}`
    : null;

  protected screenLinks(message: AssistantMessage) {
    return assistantScreenLinks(message.navigationIds ?? [], this.readContext().roles);
  }

  protected showSupport(message: AssistantMessage): boolean {
    const targets = message.navigationIds ?? [];
    // Do not add support just because an answer is unavailable or an action was
    // declined. Offer it when selected, or when no suggested screen is accessible.
    return (
      targets.includes('support') || (targets.length > 0 && this.screenLinks(message).length === 0)
    );
  }
  private followResponse = true;
  private lastScrollTop = 0;
  private lastScrollHeight = 0;
  private lastScrollPanel?: HTMLDivElement;
  private lastTouchY?: number;
  protected readonly messages = computed<readonly AssistantMessage[]>(() => [
    ...this.conversationState.messages(),
    ...this.support.messages().map(message => ({
      id: message.id, text: message.text, status: 'complete' as const, createdAt: message.time,
      role: message.role, speaker: message.speaker,
    })),
  ].sort((a, b) => (a.createdAt ?? '').localeCompare(b.createdAt ?? '')));
  protected readonly pending = this.conversationState.pending;
  protected get draft(): string {
    return this.conversationState.draft();
  }
  protected set draft(value: string) {
    if (this.pageAvailability.isSessionPaused()) return;
    this.conversationState.draft.set(value);
  }
  // Shared by the composer and send validation so the limits cannot diverge.
  protected readonly maxQuestionLength = this.conversationState.config.maxQuestionLength;
  protected readonly suggestions: { label: string; description: string; question: string; icon: IconName }[] = [
    { label: 'Upload a certificate', description: 'Files and requirements', question: 'What files do I need to upload a certificate?', icon: 'upload' },
    { label: 'Check request status', description: 'Find the right portal screen', question: 'Where can I check my request status?', icon: 'requests' },
    { label: 'Understand next steps', description: 'What happens after validation', question: 'What happens after certificate validation?', icon: 'check' },
    { label: 'Resolve an issue', description: 'Help with validation', question: 'How can I troubleshoot certificate validation?', icon: 'info' },
  ];

  constructor() {
    effect(() => {
      if (this.pageAvailability.sessionPaused()) {
        this.voice.cancel();
        // Our native dialog must not sit in the top layer over the portal warning.
        const dialog = this.endDialog?.nativeElement;
        if (dialog?.open) dialog.close();
      }
    });
    inject(DestroyRef).onDestroy(() => {
      clearTimeout(this.introductionTimer);
      this.voice.cancel();
      this.dockObserver?.disconnect();
    });
    effect((onCleanup) => {
      const authenticated = this.authenticated();
      const dismissed = this.introduction.dismissed();
      const blocked = this.pageAvailability.blocked();
      this.introductionVisible.set(false);
      if (!authenticated) {
        this.voice.cancel();
        this.open.set(false);
        return;
      }
      if (dismissed || blocked) return;
      this.introductionTimer = setTimeout(() => {
        if (!this.open() && !this.pageAvailability.isBlocked()) this.introductionVisible.set(true);
      }, this.introductionDelayMs);
      onCleanup(() => clearTimeout(this.introductionTimer));
    });
    afterEveryRender(() => {
      this.syncDockLayout();
      const panel = this.conversation?.nativeElement;
      // Follow incoming chunks unless the user has scrolled up to read earlier messages.
      if (!panel) { this.lastScrollPanel = undefined; return; }
      // Unrelated renders (including scroll events) must never reset the reader's position.
      const changed = panel !== this.lastScrollPanel || panel.scrollHeight !== this.lastScrollHeight;
      this.lastScrollPanel = panel;
      this.lastScrollHeight = panel.scrollHeight;
      if (changed && this.followResponse) {
        panel.scrollTop = this.messages().length ? panel.scrollHeight : 0;
        this.lastScrollTop = panel.scrollTop;
      }
    });
  }

  @HostListener('window:resize')
  protected syncDockLayout(): void {
    const main = this.dockArea();
    const area = this.launcherArea?.nativeElement;
    if (!main || !area || !this.authenticated()) {
      this.dockObserver?.disconnect();
      this.observedDockElements = [];
      this.lastDockClearance = 0;
      this.launcherPosition?.refreshBounds();
      return;
    }
    const mainRect = main.getBoundingClientRect();
    const left = Math.max(24, Math.min(mainRect.left + 24, window.innerWidth - 80));
    this.dockLeft.set(left);
    this.dockWidth.set(Math.max(0, window.innerWidth - left - 24));

    const bubble = this.introductionBubble?.nativeElement;
    const elements = [main, area, ...(bubble ? [bubble] : [])];
    if (typeof ResizeObserver !== 'undefined'
      && (elements.length !== this.observedDockElements.length
        || elements.some((element, index) => element !== this.observedDockElements[index]))) {
      this.dockObserver ??= new ResizeObserver(() => this.syncDockLayout());
      this.dockObserver.disconnect();
      elements.forEach(element => this.dockObserver!.observe(element));
      this.observedDockElements = elements;
    }

    // The introduction and launcher share a row. The row grows only if wrapped
    // text needs it; its viewport position includes the device safe-area inset.
    const base = window.innerHeight - area.getBoundingClientRect().top + 24;
    const clearance = Math.ceil(Math.max(104, base) / 8) * 8;
    if (clearance !== this.lastDockClearance) {
      this.lastDockClearance = clearance;
      this.dockClearanceChange.emit(clearance);
      const focused = main.ownerDocument.activeElement;
      if (focused instanceof HTMLElement && main.contains(focused)
        && !focused.closest('[aria-modal="true"]')) {
        // Wait for the shell to apply the new clearance. Keep keyboard focus in
        // view without resetting the page or moving a reader who is elsewhere.
        afterNextRender(() => {
          if (this.dockArea() !== main || main.ownerDocument.activeElement !== focused) return;
          const rect = focused.getBoundingClientRect();
          const mainBounds = main.getBoundingClientRect();
          if (rect.bottom > mainBounds.bottom - clearance || rect.top < mainBounds.top)
            focused.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
        }, { injector: this.injector });
      }
    }
    this.launcherPosition?.refreshBounds();
  }

  /** Hide evidence labels only in presentation; retain validated answers and citations in state. */
  protected displayText(message: AssistantMessage): string {
    if (message.role !== 'assistant') return message.text;
    let text = message.text.replace(/[ \t]*\[S\d+\]/g, '');
    // Network chunks may split a citation marker. Hold its unfinished suffix until complete.
    if (message.status === 'pending') text = text.replace(/[ \t]*\[(?:S\d*)?$/, '');
    return text;
  }

  protected trackConversationScroll(): void {
    const panel = this.conversation?.nativeElement;
    if (!panel) return;
    if (panel.scrollTop < this.lastScrollTop) this.followResponse = false;
    else if (panel.scrollTop > this.lastScrollTop &&
             panel.scrollHeight - panel.scrollTop - panel.clientHeight <= 2) this.followResponse = true;
    this.lastScrollTop = panel.scrollTop;
  }

  protected onConversationWheel(event: WheelEvent): void {
    if (event.deltaY < 0) this.followResponse = false;
  }

  protected onConversationKey(event: KeyboardEvent): void {
    if (['ArrowUp', 'PageUp', 'Home'].includes(event.key) || (event.key === ' ' && event.shiftKey))
      this.followResponse = false;
  }

  protected onConversationTouch(event: TouchEvent): void {
    const y = event.touches[0]?.clientY;
    if (event.type === 'touchmove' && y !== undefined && this.lastTouchY !== undefined && y > this.lastTouchY)
      this.followResponse = false;
    this.lastTouchY = y;
  }

  toggle(): void {
    if (!this.authenticated() || this.pageAvailability.isBlocked()) return;
    if (this.launcherPosition?.consumeDragClick()) return;
    if (this.open()) this.minimize();
    else {
      this.dismissIntroduction();
      this.open.set(true);
      afterNextRender(() => this.composer?.nativeElement.focus({ preventScroll: true }), { injector: this.injector });
    }
  }

  minimize(): void {
    this.voice.cancel();
    this.open.set(false);
    this.launcher?.nativeElement.focus();
  }

  /** Closing ends the local session; minimizing and navigation preserve it. */
  protected close(): void {
    this.voice.cancel();
    if (this.support.active() || this.messages().length > 0 || this.draft.trim()) {
      // A native modal supplies focus containment and makes the portal inert;
      // its content and styling belong to Hub Central, not a browser alert.
      this.endDialog?.nativeElement.showModal();
      return;
    }
    this.endChat();
  }

  protected keepChatting(): void {
    this.endDialog?.nativeElement.close();
    this.composer?.nativeElement.focus({ preventScroll: true });
  }

  protected endChat(): void {
    this.endDialog?.nativeElement.close();
    this.reset();
    this.maximized.set(false);
    this.minimize();
  }

  protected dismissIntroduction(): void {
    clearTimeout(this.introductionTimer);
    this.introductionVisible.set(false);
    this.introduction.dismiss();
  }

  reset(): void {
    this.followResponse = true;
    this.conversationState.reset();
    this.composer?.nativeElement.focus({ preventScroll: true });
  }

  protected startSupport(): void {
    if (this.pageAvailability.isSessionPaused()) return;
    this.conversationState.startSupport();
  }

  protected send(): void {
    if (this.pageAvailability.isSessionPaused()) return;
    if (this.voice.active()) return;
    if (this.support.phase() !== 'idle') {
      const draft = this.draft;
      void this.support.send(draft).then(sent => { if (sent && this.draft === draft) this.draft = ''; });
      return;
    }
    if (!this.conversationState.send()) return;
    this.followResponse = true;
    this.composer?.nativeElement.focus({ preventScroll: true });
    afterNextRender(
      () => {
        const panel = this.conversation?.nativeElement;
        if (panel) panel.scrollTop = panel.scrollHeight;
      },
      { injector: this.injector },
    );
  }

  protected onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
      event.preventDefault();
      this.send();
    }
  }

  @HostListener('document:visibilitychange')
  protected onVisibilityChange(): void {
    if (document.hidden) this.voice.cancel();
  }

  protected toggleVoiceConversation(): void {
    if (this.pageAvailability.isSessionPaused()) return;
    if (this.voice.active()) {
      this.voice.cancel();
      return;
    }
    if (this.pending() || this.support.phase() !== 'idle') return;
    if (this.draft.trim()) {
      this.voice.notice.set(
        'Send or clear your typed question before starting voice conversation.',
      );
      return;
    }
    this.voice.startConversation((text) => {
      if (this.pageAvailability.isSessionPaused()) return false;
      this.draft = text;
      this.followResponse = true;
      return this.conversationState.send();
    }, this.maxQuestionLength);
  }
}
